import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useSEO({
    title: "Reset your password — ErrandDay",
    description: "Forgot your ErrandDay password? Enter your email and we'll send a secure, time-limited reset link.",
    noindex: true,
    canonicalPath: "/forgot-password",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md eb-fadein">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[var(--eb-primary)] flex items-center justify-center text-white font-black">ED</div>
          <span className="font-[800] text-lg">ErrandDay</span>
        </Link>
        {sent ? (
          <div className="eb-card p-7 text-center">
            <CheckCircle2 className="w-10 h-10 text-[var(--eb-primary)] mx-auto" />
            <h2 className="mt-4 text-2xl">Check your inbox</h2>
            <p className="mt-3 text-[var(--eb-text-dim)]">
              If <b className="text-[var(--eb-text)]">{email}</b> is registered, we&apos;ve sent a secure reset link. It expires in 30 minutes.
            </p>
            <Link to="/login" className="eb-btn eb-btn-ghost mt-6 inline-flex">Back to sign in</Link>
          </div>
        ) : (
          <>
            <span className="eb-chip">Password reset</span>
            <h1 className="mt-4 text-3xl">Reset your ErrandDay password</h1>
            <p className="mt-3 text-[var(--eb-text-dim)] text-sm leading-relaxed">
              We&apos;ll email you a secure, single-use link that expires in 30 minutes.
              No password is ever sent over email, and your account stays signed-out everywhere until you set a new one.
              Tip: check spam if it doesn&apos;t arrive within a couple of minutes.
            </p>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="eb-label">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@diaspora.com" className="eb-input"
                />
              </div>
              {err && <div className="text-sm text-[var(--eb-secondary)]">{err}</div>}
              <button data-testid="forgot-submit-btn" disabled={busy} type="submit" className="eb-btn eb-btn-primary w-full !py-3">
                {busy ? "Sending…" : (<><Mail className="w-4 h-4" /> Send reset link</>)}
              </button>
            </form>
            <div className="mt-5 text-sm text-center text-[var(--eb-text-dim)]">
              Remember it now? <Link to="/login" className="text-[var(--eb-primary)] hover:underline">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
